import { jsPDF } from 'jspdf';
import type { CustomerAgentInfo } from './api';
import { customerAssistantDisplayName } from './customerCopy';
export type TranscriptPdfMessage = {
  author: 'Agent' | 'You' | 'Live Perception';
  text: string;
  time: string;
  kind?: 'link' | 'text' | 'live_perception_debug';
};

const brand = [9, 104, 240] as const;
const navy = [15, 47, 106] as const;
const muted = [113, 130, 152] as const;

export type CustomerTranscriptPdfInput = {
  sessionId: string;
  displayId: string;
  participantName: string;
  durationLabel: string;
  agentInfo: CustomerAgentInfo | null;
  messages: TranscriptPdfMessage[];
};

async function loadRasterDataUrl(url: string, size = 128): Promise<string | null> {
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) return null;
    const blob = await res.blob();
    const bmp = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const scale = Math.max(size / bmp.width, size / bmp.height);
    const w = bmp.width * scale;
    const h = bmp.height * scale;
    ctx.drawImage(bmp, (size - w) / 2, (size - h) / 2, w, h);
    bmp.close();
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

function drawLogoFallback(doc: jsPDF, x: number, y: number, px: number) {
  doc.setFillColor(...brand);
  doc.roundedRect(x, y, px, px, px * 0.22, px * 0.22, 'F');
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(px * 0.08);
  const top = y + px * 0.22;
  const bot = y + px * 0.78;
  const bars = [0.18, 0.3, 0.42, 0.54, 0.66, 0.78];
  const heights = [0.35, 0.55, 0.75, 0.65, 0.45, 0.35];
  bars.forEach((b, i) => {
    const bx = x + px * b;
    const h = px * heights[i];
    const by = top + (bot - top - h) / 2;
    doc.line(bx, by, bx, by + h);
  });
  doc.setDrawColor(0, 0, 0);
}

function ensureSpace(doc: jsPDF, y: number, need: number, margin: number): number {
  const pageH = doc.internal.pageSize.getHeight();
  if (y + need <= pageH - 56) return y;
  doc.addPage();
  return margin + 12;
}

export async function downloadCustomerTranscriptPdf(input: CustomerTranscriptPdfInput) {
  const {
    sessionId,
    displayId,
    participantName,
    durationLabel,
    agentInfo,
    messages,
  } = input;
  const agentName = customerAssistantDisplayName(agentInfo?.name);
  const agentRole = agentInfo?.role || 'Conversation';

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 44;
  let y = 0;

  const logoData = await loadRasterDataUrl(`${import.meta.env.BASE_URL || '/'}favicon.svg`, 64);
  const avatarData = agentInfo?.avatarUrl?.trim()
    ? await loadRasterDataUrl(agentInfo.avatarUrl.trim(), 96)
    : null;

  // Header band
  doc.setFillColor(...brand);
  doc.rect(0, 0, pageW, 92, 'F');
  const logoSize = 36;
  const logoY = 28;
  if (logoData) {
    doc.addImage(logoData, 'PNG', margin, logoY, logoSize, logoSize);
  } else {
    drawLogoFallback(doc, margin, logoY, logoSize);
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('Igentify Pulse', margin + logoSize + 14, logoY + 16);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text('Session transcript', margin + logoSize + 14, logoY + 34);
  doc.setFontSize(9);
  doc.text(`Session ${displayId}`, margin + logoSize + 14, logoY + 48);

  y = 112;

  // Agent card
  const cardH = 72;
  doc.setFillColor(248, 250, 254);
  doc.setDrawColor(226, 234, 246);
  doc.setLineWidth(1);
  doc.roundedRect(margin, y, pageW - margin * 2, cardH, 8, 8, 'FD');
  const avatarSize = 48;
  const avatarX = margin + 16;
  const avatarY = y + (cardH - avatarSize) / 2;
  if (avatarData) {
    doc.addImage(avatarData, 'PNG', avatarX, avatarY, avatarSize, avatarSize);
  } else {
    doc.setFillColor(...brand);
    doc.circle(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text((agentName[0] || 'A').toUpperCase(), avatarX + avatarSize / 2, avatarY + avatarSize / 2 + 6, {
      align: 'center',
    });
  }
  const textX = avatarX + avatarSize + 14;
  doc.setTextColor(...navy);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(agentName, textX, y + 28);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...muted);
  doc.text(agentRole, textX, y + 44);
  doc.setFontSize(9);
  doc.text(`Participant: ${participantName}  ·  Duration: ${durationLabel}`, textX, y + 58);

  y += cardH + 28;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...navy);
  doc.text('Conversation', margin, y);
  y += 20;

  const transcriptMessages = messages.filter(
    (m) => m.kind !== 'live_perception_debug' && m.author !== 'Live Perception' && m.text?.trim(),
  );

  if (!transcriptMessages.length) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...muted);
    doc.text('No transcript messages for this session.', margin, y);
    y += 20;
  }

  for (const m of transcriptMessages) {
    const isUser = m.author === 'You';
    const speaker = isUser ? participantName : agentName;
    const stamp = m.time || '';
    const body = m.text.trim();
    const header = stamp ? `${speaker}  ·  ${stamp}` : speaker;
    const bodyLines = doc.splitTextToSize(body, pageW - margin * 2 - 24);
    const blockH = 14 + bodyLines.length * 13 + 12;
    y = ensureSpace(doc, y, blockH, margin);

    if (isUser) doc.setFillColor(240, 244, 250);
    else doc.setFillColor(232, 242, 255);
    doc.roundedRect(margin, y - 4, pageW - margin * 2, blockH, 6, 6, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...muted);
    doc.text(header, margin + 12, y + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...navy);
    doc.text(bodyLines, margin + 12, y + 26);
    y += blockH + 10;
  }

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...muted);
    doc.text(
      `Igentify Pulse · ${sessionId} · page ${i} of ${pages}`,
      margin,
      doc.internal.pageSize.getHeight() - 28,
    );
    doc.setFontSize(7);
    doc.text('Confidential — for your personal records.', margin, doc.internal.pageSize.getHeight() - 16);
  }

  doc.save(`pulse-transcript-${displayId}.pdf`);
}
